import { useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { SmartImage } from '../components/ui/SmartImage'

export default function LoginPage() {
  const { user, loading, login } = useAuth()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState('')

  // SSO params from URL (set when redirected from archive site)
  const isSSOFlow = searchParams.get('sso') === '1'
  const ssoClientId = searchParams.get('client_id')
  const ssoRedirectUri = searchParams.get('redirect_uri')
  const ssoState = searchParams.get('state')

  // Map client IDs to friendly archive names
  const archiveNames: Record<string, string> = {
    'origin-archive': 'The Origin Archive',
    'conflict-archive': 'The Conflict Archive',
    'treaty-archive': 'The Treaty Archive',
    'lineage-archive': 'The Lineage Archive',
    'civilization-archive': 'The Civilization Archive',
    'classified-archive': 'The Classified Archive',
    'exploration-archive': 'The Exploration Archive',
    'mythology-archive': 'The Mythology Archive',
  }
  const archiveName = ssoClientId ? archiveNames[ssoClientId] || ssoClientId : null

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-archive-gold border-t-transparent" />
      </div>
    )
  }

  // If user is already logged in on hub and this is an SSO flow,
  // redirect back to authorize endpoint to get auth code
  if (user && isSSOFlow && ssoClientId && ssoRedirectUri) {
    const authorizeUrl = `/api/auth/sso/authorize?client_id=${encodeURIComponent(ssoClientId)}&redirect_uri=${encodeURIComponent(ssoRedirectUri)}${ssoState ? `&state=${encodeURIComponent(ssoState)}` : ''}`
    window.location.href = authorizeUrl
    return null
  }

  if (user) {
    return <Navigate to="/profile" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('sending')
    setError('')

    // Pass SSO params if present
    const ssoParams = isSSOFlow && ssoClientId && ssoRedirectUri
      ? {
          sso_client_id: ssoClientId,
          sso_redirect_uri: ssoRedirectUri,
          sso_state: ssoState || undefined,
        }
      : undefined

    const result = await login(email, ssoParams)

    if (result.success) {
      setStatus('sent')
    } else {
      setStatus('error')
      setError(result.error || 'Something went wrong')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="artifact-frame p-8 sm:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full overflow-hidden border border-archive-gold/30">
                <SmartImage
                    src="/images/archive-network-seal.png"
                    alt="The Archive Network"
                    sizes="54px"
                    width={54}
                    height={54}
                    loading="eager"
                    className="w-full h-full object-cover"
                  />
              </div>
            </Link>
          </div>

          {status === 'sent' ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-archive-gold/10 flex items-center justify-center">
                <svg className="w-8 h-8 text-archive-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="font-display text-2xl text-archive-cream mb-4">
                Check Your Email
              </h1>
              <p className="text-archive-muted mb-6">
                We've sent a magic link to <span className="text-archive-cream">{email}</span>.
                Click the link in the email to sign in.
              </p>
              <p className="text-sm text-archive-muted">
                Didn't receive it?{' '}
                <button
                  onClick={() => setStatus('idle')}
                  className="text-archive-gold hover:text-archive-amber"
                >
                  Try again
                </button>
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl text-archive-cream text-center mb-2">
                Sign In
              </h1>
              <p className="text-archive-muted text-center mb-8">
                {isSSOFlow ? (
                  <>Sign in with your Archive Network account</>
                ) : (
                  <>Enter your email to receive a magic sign-in link</>
                )}
              </p>
              {isSSOFlow && archiveName && (
                <div className="mb-6 p-3 bg-archive-slate/50 rounded-lg border border-archive-stone/30 text-center">
                  <p className="text-sm text-archive-muted">
                    You'll be redirected back to
                  </p>
                  <p className="text-archive-cream text-sm font-medium">
                    {archiveName}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm text-archive-muted mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="w-full px-4 py-3 bg-archive-slate border border-archive-stone rounded-lg text-archive-cream placeholder-archive-muted/50 focus:outline-none focus:border-archive-gold transition-colors"
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-sm text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-3 bg-gradient-to-r from-archive-gold to-archive-amber text-archive-black font-semibold rounded-lg hover:shadow-lg hover:shadow-archive-gold/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Magic Link'
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-archive-stone/30">
                <p className="text-center text-sm text-archive-muted mb-2">
                  <span className="text-archive-gold font-medium">One account for all archives.</span>
                </p>
                <p className="text-center text-xs text-archive-muted/70">
                  Your Archive Network account works across The Origin Archive, The Conflict Archive, The Treaty Archive, The Lineage Archive, The Civilization Archive, The Classified Archive, The Exploration Archive, and The Mythology Archive.
                </p>
              </div>

              <p className="mt-4 text-center text-sm text-archive-muted">
                No account? One will be created for you automatically.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
